import { ButtonProps, ConfigProvider, Dropdown, MenuProps, Popover, Space, Typography, theme } from 'antd';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { PropsWithChildren, ReactElement, cloneElement, useCallback, useContext, useMemo } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import useCurrentTicketStatus from '../hooks/ticketDetailPage/useCurrentTicketStatus';
import useRequiredFormFields from '../hooks/ticketDetailPage/useRequiredFormFields';
import { BreadCrumbsViewState } from '../layouts/Pages/TicketPagesContentLayout/TicketDetailContentLayout';
import {
    TICKET_FORM_FIELDS_KEY,
    TicketSystemFieldsKeyMap,
    reverseStatusMap,
    status,
    statusMap,
    statusOptions,
    statusi18nKeyMap
} from '../model/types';
import { useTicketFormUtilStore } from '../store/RootStoreContext';
import { BasicFormMethodsContext } from '../store/provider/BasicFormMethodsProvider';
import { tagBackgroundColors } from '../styles/theme';
import { defineMode } from '../utils/forms/formHelpers';
const { Text } = Typography;

function ErrorPopover({
    isSubmitButtonDisabled,
    children
}: PropsWithChildren<{
    isSubmitButtonDisabled: boolean;
}>) {
    const { t } = useTranslation('forms');
    const { dirtyFields } = useFormState();
    const {
        token: { marginMD }
    } = theme.useToken();

    const requiredToSubmitFields = useRequiredFormFields();

    return (
        <Popover
            key={dirtyFields.toString() + isSubmitButtonDisabled.toString()}
            title={t('requiredFieldTitle')}
            content={
                <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                    {requiredToSubmitFields.map((field) => {
                        return (
                            <p key={field.key}>
                                <span style={{ color: 'red' }}>* </span>
                                {field.translatedName}
                            </p>
                        );
                    })}
                </div>
            }
            placement="topRight"
            destroyTooltipOnHide
            trigger={isSubmitButtonDisabled && requiredToSubmitFields?.length > 0 ? 'hover' : []}
            align={{ offset: [-marginMD, -marginMD / 2] }}
        >
            {children}
        </Popover>
    );
}

function SubmitButton({ viewOption }: { viewOption?: BreadCrumbsViewState }) {
    const { asPath } = useRouter();
    const { t: tCommon } = useTranslation('common');

    const { submit } = useContext(BasicFormMethodsContext);
    const { isUploadingAttachment } = useTicketFormUtilStore();
    const ticketStatus = useCurrentTicketStatus();
    const { setValue } = useFormContext();
    const { isSubmitting, isDirty, isValid } = useFormState();
    // const { isLoading: isLoadingEvents } = useCurrentTicketEvents();
    const mode = defineMode(asPath);

    const handleOnClick = useCallback(
        (key: keyof typeof statusMap): void => {
            setValue(`${TICKET_FORM_FIELDS_KEY}.${TicketSystemFieldsKeyMap.status}`, key);
            submit();
        },
        [setValue, submit]
    );

    const handleOnClickSubmitButton: ButtonProps['onClick'] = useCallback(() => {
        if (mode === 'create')
            setValue(
                `${TICKET_FORM_FIELDS_KEY}.${TicketSystemFieldsKeyMap.status}`,
                reverseStatusMap[statusOptions.New]
            );
        else setValue(`${TICKET_FORM_FIELDS_KEY}.${TicketSystemFieldsKeyMap.status}`, reverseStatusMap[ticketStatus]);
        submit();
    }, [mode, setValue, submit, ticketStatus]);

    const isTicketClosed = ticketStatus === statusOptions.Closed;
    const isTicketNotNew = ticketStatus !== statusOptions.New;
    const isTicketSolved = ticketStatus === statusOptions.Solved;

    const isTicketView = viewOption === BreadCrumbsViewState.ticketView;

    const isSubmitButtonDisabled =
        // (mode === 'edit' && isLoadingEvents) ||
        (mode === 'edit' && !ticketStatus) ||
        isTicketClosed ||
        !isTicketView ||
        isUploadingAttachment ||
        isSubmitting ||
        !isDirty ||
        !isValid;

    const isDropdownButtonDisabled =
        !isTicketView ||
        isUploadingAttachment ||
        isSubmitting ||
        (mode === 'create' && !isDirty) ||
        isTicketClosed ||
        (!isValid && !isTicketSolved);
    const isButtonSelfDisabled = !isTicketView || isSubmitting || (!isValid && !isTicketSolved);

    const translatedStatus = tCommon(statusi18nKeyMap?.[ticketStatus ?? statusOptions.New]);

    const items: MenuProps['items'] = useMemo(
        () =>
            Object.keys(statusOptions)
                .filter((status: status) => status !== statusOptions.Closed)
                .filter((status: status) => ticketStatus !== status)
                .filter((status: status) => !(isTicketNotNew && status === statusOptions.New))
                .map((status: status) => ({
                    key: reverseStatusMap[status],
                    label: (
                        <Space size="large" style={{ paddingLeft: 5 }}>
                            <div
                                style={{
                                    backgroundColor: tagBackgroundColors[status],
                                    width: 14,
                                    height: 14,
                                    borderRadius: 4
                                }}
                            />
                            <Text style={{ backgroundColor: 'transparent' }}>
                                <Trans
                                    ns="tickets"
                                    i18nKey="submit"
                                    components={[<Text strong key={1} />]}
                                    values={{
                                        status: tCommon((statusi18nKeyMap?.[status] as any) ?? 'New')
                                    }}
                                />
                            </Text>
                        </Space>
                    )
                })),
        [isTicketNotNew, tCommon, ticketStatus]
    );

    const colorPrimary = tagBackgroundColors[ticketStatus ?? statusOptions.New];

    return (
        <ConfigProvider theme={{ token: { colorPrimary } }}>
            <ErrorPopover isSubmitButtonDisabled={isSubmitButtonDisabled}>
                <Dropdown.Button
                    buttonsRender={([leftButton, rightButton]) => [
                        cloneElement(leftButton as ReactElement<ButtonProps>, {
                            children: (
                                <Trans
                                    ns="tickets"
                                    i18nKey="submit"
                                    components={[<Text strong key={1} style={{ color: 'inherit' }} />]}
                                    values={{
                                        status: translatedStatus
                                    }}
                                />
                            ),
                            disabled: isSubmitButtonDisabled,
                            type: 'primary',
                            onClick: handleOnClickSubmitButton
                        }),
                        rightButton
                    ]}
                    type="primary"
                    trigger={['click']}
                    menu={{
                        items,
                        onClick: ({ key }) => handleOnClick(key as unknown as keyof typeof statusMap)
                    }}
                    onClick={handleOnClickSubmitButton}
                    disabled={isButtonSelfDisabled || isDropdownButtonDisabled} //right button not become disabled because wrapped dropdown.
                />
            </ErrorPopover>
        </ConfigProvider>
    );
}

export default observer(SubmitButton);
